import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const OAuthCallback: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');

  const success = searchParams.get('success') === 'true';
  const provider = searchParams.get('provider');
  const error = searchParams.get('error');

  useEffect(() => {
    if (success) {
      setStatus('success');
      // Close the popup after a short delay
      setTimeout(() => {
        window.close();
      }, 1500);
    } else {
      setStatus('error');
      // Close the popup after showing error
      setTimeout(() => {
        window.close();
      }, 3000);
    }
  }, [success]);

  const providerName = provider?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'OAuth';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full p-8 bg-white rounded-lg shadow-lg text-center">
        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('oauth.processing')}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('oauth.success', { provider: providerName })}
            </h2>
            <p className="text-gray-600">{t('oauth.closingWindow')}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('oauth.failed', { provider: providerName })}
            </h2>
            {error && <p className="text-red-600 text-sm mb-2">{error}</p>}
            <p className="text-gray-600">{t('oauth.closingWindow')}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
